import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import throttle from 'lodash/throttle';
import Typography from '@latitude/typography';
import { REBRAND_COLOR, BREAKPOINT } from '@latitude/core/utils/constants';
import Section from '@latitude/section-rebrand';
import Button from '@latitude/button-rebrand';
import SvgInline from '@latitude/svg-inline';

/* ==========================================================================
   Styled Components
   ========================================================================== */

const StickyNavigationPlaceholder = styled.div``;

const StickyNavigationWrapper = styled.nav`
  background-color: ${({ background }) => background || '#fff'};
  overflow: hidden;
  width: 100%;
  z-index: 10;

  ${({ active }) =>
    active &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
    `}

  @media (max-width: ${BREAKPOINT.LG}) {
    display: ${({ active }) => (active ? 'block' : 'none')};
  }
`;

const StickyNavigationInner = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  align-items: stretch;
`;

const NavItems = styled.ul`
 display: none;
 @media (min-width: ${BREAKPOINT.LG}) {
    display: flex;
  }
  margin: 0;
  padding: 0;
  list-style-type: none;
  align-items: stretch;

  overflow-x: auto;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const NavItem = styled.li`
  height: 100%;
  display: flex;
  align-items: stretch;
  margin-right: 16px;

  &:last-child {
    margin-right: 0;
  }

  a {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 2px;

    text-decoration: none;
    border-bottom: 2px solid transparent;

    color: inherit;
    transition: color 0.2s ease, border-color 0.2s ease;
  }

  /* Hover */
  &:hover a {
    color: ${REBRAND_COLOR.MID_BLUE};
    text-shadow: none;
  }

  /* Active */
  ${({ active }) =>
    active &&
    css`
      a {
        color: ${REBRAND_COLOR.MID_BLUE};
        border-bottom: 2px solid ${REBRAND_COLOR.MID_BLUE};
      }
      .typography-base__link {
        color: ${REBRAND_COLOR.MID_BLUE} !important;
      }
    `}
  .typography-base__link {
    color: ${REBRAND_COLOR.INDIGO};
    border-bottom: none;
  }
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
}
`;

const CtaGroup = styled.div`
  display: flex;
    visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
  padding: 5px 0;
  a {
    border-bottom: none;
    color: ${REBRAND_COLOR.INDIGO};
  }
  a:hover {
    border-bottom: none;
    text-shadow: none;
    color: ${REBRAND_COLOR.MID_BLUE};
  }
  @media (max-width: ${BREAKPOINT.LG}) {
    justify-content: flex-end;
    padding: 5px 0px;
    margin: 0 0 0 auto;
    overflow-x: hidden;
    a span {
      font-size: 14px !important;
    }
  }
`;

const PhoneLink = styled.a`
  display: flex;
  flex: 1 0 auto;
  justify-content: flex-end;
  align-items: center;
  white-space: nowrap;
  text-decoration: none;
  padding-right: 1rem;
`;

const PhoneIcon = styled.span`
  width: 40px;
  height: 40px;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ==========================================================================
   Component
   ========================================================================== */

class StickyNavigation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      active: false,
      offset: 0,
      placeholderHeight: 0,
      activeLabel: null
    };

    this.stickyNavRef = React.createRef();
    this.placeholderRef = React.createRef();

    this.scrollHandler = throttle(() => {
      if (!this.stickyNavRef.current) return;

      const isActive = window.pageYOffset > this.getTriggerPosition();

      this.setState({
        active: isActive,
        offset: isActive ? this.getOffsetPosition() : 0,
        placeholderHeight: isActive ? this.getPlaceholderHeight() : 0
      });
    }, 1);
  }

  componentDidMount() {
    document.addEventListener('scroll', this.scrollHandler);
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.scrollHandler);
  }

  getTriggerPosition = () => {
    const triggerElem = document.querySelector(
      this.props.triggerElem || '[data-sticky-navigation-placeholder]'
    );

    if (!triggerElem) return 0;

    return (
      triggerElem.getBoundingClientRect().top +
      window.pageYOffset -
      this.getOffsetPosition()
    );
  };

  getPlaceholderHeight = () => {
    return this.stickyNavRef.current
      ? this.stickyNavRef.current.offsetHeight || 0
      : 0;
  };

  getOffsetPosition = () => {
    let offsetElem = null;

    if (this.props.offsetElem) {
      offsetElem = document.querySelector(this.props.offsetElem);
    }

    return offsetElem ? offsetElem.offsetHeight || 0 : 0;
  };

  handleClickNavItem = (anchor, label) => {
    this.setState({ activeLabel: label });
  window.history.replaceState(null, '', `#${anchor}`);

    const anchorElem = document.querySelector(`#${anchor}`);

    if (anchorElem) {
      window.scrollTo({
        top:
          anchorElem.getBoundingClientRect().top +
          window.pageYOffset -
          this.getOffsetPosition() -
          this.getPlaceholderHeight(),
        behavior: 'smooth'
      });
    }
  };

  render() {
    const {
      className,
      background,
      items,
      phoneNumber,
      ctaHref,
      ctaText,
      children
    } = this.props;

    const { active, offset, placeholderHeight, activeLabel } = this.state;

    return (
      <>
        <StickyNavigationPlaceholder
          ref={this.placeholderRef}
          data-sticky-navigation-placeholder
          style={{ height: placeholderHeight }}
        />

        <StickyNavigationWrapper
          ref={this.stickyNavRef}
          active={active}
          className={className}
          background={background?.value}
          style={{
            transform: `translateY(${offset}px)`
          }}
        >
          <Section>
            {children ? (
              children
            ) : (
              <StickyNavigationInner>
                <NavItems>
                  {items.map(item => (
                    <NavItem
                      key={item.label}
                      active={activeLabel === item.label}
                    >
                      <NavLink
                        href={`#${item.anchor}`}
                        onClick={e => {
                          e.preventDefault();
                          this.handleClickNavItem(item.anchor, item.label);
                        }}
                      >
                        <Typography.LinkSmall>
                          {item.label}
                        </Typography.LinkSmall>
                      </NavLink>
                    </NavItem>
                  ))}
                </NavItems>

                <CtaGroup active={active}>
                  {phoneNumber && (
                    <PhoneLink href={`tel:${phoneNumber}`}>
                      <PhoneIcon>  <SvgInline name="icon-phone-rebrand" className="loan-box__icon" /></PhoneIcon>
                      <Typography.LinkSmall>{phoneNumber}</Typography.LinkSmall>
                    </PhoneLink>
                  )}

                  {ctaHref && (
                    <Button
                      variant="primary"
                      size="md"
                      ctaText={ctaText || 'Apply Now'}
                      ctaUrl={ctaHref}
                      trackEventData={{
                        category: 'text-link',
                        location: 'sticky-navigation',
                        label: ctaText || 'Apply Now'
                      }}
                    />
                  )}
                </CtaGroup>
              </StickyNavigationInner>
            )}
          </Section>
        </StickyNavigationWrapper>
      </>
    );
  }
}

/* ==========================================================================
   PropTypes
   ========================================================================== */

StickyNavigation.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      anchor: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  phoneNumber: PropTypes.string,
  ctaHref: PropTypes.string,
  ctaText: PropTypes.string,
  offsetElem: PropTypes.string,
  triggerElem: PropTypes.string,
  children: PropTypes.node
};

StickyNavigation.defaultProps = {
  className: '',
  items: [],
  phoneNumber: '',
  ctaHref: '',
  ctaText: 'Apply Now',
  offsetElem: '',
  triggerElem: '[data-sticky-navigation-placeholder]',
  children: null
};

export default StickyNavigation;